/* 150 §3 — THE TYPE-AHEAD, EXTRACTED FROM THE MATTER PICKER.
 *
 * The user filter needs "recents first, then autocomplete", and the brief's
 * instruction is "point MatterPicker's shape at collaborators; do not invent a
 * second type-ahead". So the shape moves here — the input, the completion
 * machinery, the anchored portal menu, the recents/search arms, the caps and
 * the "+N more" honesty row — and `MatterPicker.tsx` becomes the wrapper that
 * owns everything matter-specific: the ca/cc merge, the assigned/archived
 * filters, the recents-id resolution, the selected-label disambiguation.
 *
 * IT IS AN EXTRACTION (rule 3): every constant, cap, arm and edge below is
 * MatterPicker's own, moved verbatim; the only new thing is the option shape
 * (`{ key, label, mono }`, where the matter wrapper maps title → label and
 * ref → mono, so `matchesFor` still searches exactly `title + ref`).
 * `inputClassName`/`inputStyle` default to the picker's current dress so the
 * five matter hosts render pixel-identically; the filter hosts pass their own.
 *
 * The 131/133/134 history that explains each piece stays in MatterPicker.tsx —
 * it is the file those briefs were about; pointers here are one-line.
 */
// 210 §1 — `import React` is the house remedy for react/react-in-jsx-scope
// (SharedUI.tsx says so): the rule counts per JSX element, and the not-loaded
// arm below adds two.
import React from "react";
import { useMemo, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties } from "react";
import type { TFn } from "../lib/taxonomyTypes";
import { FS, RAD, Z } from "../lib/tokens";
import { useMenuLevel } from "../lib/paneStacking"; // 204 §1
import { useAnchoredMenu } from "../hooks/useAnchoredMenu";
import { ScrollHint, useScrollHint, ListNotLoaded } from "./SharedUI";
import { useListsNotLoaded } from "../lib/listLoad"; // 210 §1
import { NO_AUTOFILL, noAutofillClass } from "../lib/noAutofill"; // 213B §8 — no sign-ins, no contacts, in a picker

/* 134 §1(b) — five rows on the click arm (recents where they exist, else the
   first five); seven on the search arm (the two originals' number).
   151D §1(b) — the full-list arm leads with TEN recents (Laurent's number);
   the legacy either/or arm keeps its five. */
const CLICK_CAP = 5;
const OPEN_RECENTS_CAP = 10;
const SEARCH_CAP = 7;

const HEAD_ROW: CSSProperties = {
  padding: "4px 10px 2px",
  fontSize: FS.s8,
  color: "var(--tm)",
  textTransform: "uppercase",
  letterSpacing: ".4px",
  fontWeight: 600,
};

/* 133 §1 — a cap that does not say so is an option the user concludes does not
   exist; the row reports on both arms and is deliberately not clickable. */
const MORE_ROW: CSSProperties = {
  padding: "5px 10px",
  fontSize: FS.s9,
  color: "var(--tm)",
  borderTop: "1px solid var(--br)",
};

/* 134 §1 — portalled to <body>, placed by useAnchoredMenu after it measures
   (hidden until then); z-index 201 clears .modal-overlay (100).
   151F §2 — EXPORTED (with BACKDROP and MENU_MIN_W): MenuSelect is the
   select face over this same popover, and sharing the dress rather than
   copying it is what keeps "one idiom" true. */
export const MENU_BOX: CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  visibility: "hidden",
  background: "var(--bc)",
  border: "1px solid var(--br)",
  borderRadius: RAD.r8,
  boxShadow: "0 6px 20px var(--shd)",
  zIndex: Z.popover,
  /* 151D §1(b) — deep enough for the Recent head + ten recent rows at the
     default text size; the complete list follows by scrolling and the
     hint arrows say so. Approximate BY DESIGN: at larger text sizes fewer
     rows fit and the down arrow still marks the continuation. */
  maxHeight: 292,
  overflowY: "auto",
};

export const BACKDROP: CSSProperties = { position: "fixed", inset: 0, zIndex: Z.popoverBackdrop };

/* 134 §1 — the menu's floor width; a portalled menu is not bound by the box it
   escaped. */
export const MENU_MIN_W = 260;

export type TypeaheadOption = {
  /** stable identity — what onPick hands back is the whole option */
  key: string;
  /** the searchable, completable text (a matter's title, a person's name) */
  label: string;
  /** the right-aligned monospace column (a matter's ref); also searchable */
  mono?: string;
};

export type TypeaheadPickerProps<T extends TypeaheadOption> = {
  t: TFn;
  /** the filtered, sorted population — the wrapper owns filters and order */
  options: T[];
  /** what the closed input shows when something is chosen (wrapper resolves) */
  selectedLabel?: string;
  /** `null` = closed; `""` = open on recents; else = search (the C18 contract).
   *  Omit both and the picker owns the state itself. */
  query?: string | null;
  setQuery?: (v: string | null) => void;
  placeholder: string;
  onPick: (o: T) => void;
  /** fired when the field is emptied by typing, and by the clear row */
  onClear: () => void;
  /** the recents arm, called inside the model memo exactly where the matter
   *  picker called getRecentCases — resolved options, uncapped; the cap and
   *  the else-first-five fallback live here */
  getRecents?: () => T[];
  /** present ⇒ the clear row renders above the list */
  clearLabel?: string;
  /** the "+N more — type to narrow" row, resolved at the call site so each
   *  host keeps its own i18n key as a literal the census can see */
  moreLabel: (n: number) => string;
  /** 151C §1 — the click arm opens on the COMPLETE population (recents may
   *  lead, under their head row; the full list follows, and the menu's own
   *  max-height scrolls it). The search arm keeps its cap and its honesty
   *  row — typing is already the narrowing. Opt-in: the matter hosts keep
   *  the capped click arm 131/133/134 ruled for populations of hundreds. */
  fullListOnOpen?: boolean;
  inputClassName?: string;
  inputStyle?: CSSProperties;
  /** the position:relative wrapper's extra style — a host that puts the picker
   *  inside a flex shell (`.search-box`) needs `flex:1; min-width:0` on it */
  wrapperStyle?: CSSProperties;
  /** 210 §1 — the lists the population comes from (their `SK` keys). While one
   *  of them could not load, the picker offers nothing: it says so in its
   *  place, with « Réessayer » (ListNotLoaded) — never a list with one of its
   *  halves missing. */
  lists?: readonly string[];
};
const NO_LISTS: readonly string[] = [];

export default function TypeaheadPicker<T extends TypeaheadOption>({
  t,
  options,
  selectedLabel,
  query,
  setQuery,
  placeholder,
  onPick,
  onClear,
  getRecents,
  clearLabel,
  moreLabel,
  fullListOnOpen,
  inputClassName = "field-input",
  inputStyle,
  wrapperStyle,
  lists = NO_LISTS,
}: TypeaheadPickerProps<T>) {
  const unloaded = useListsNotLoaded(lists);
  /* Always called, never conditionally: the hook runs whether or not the caller
     supplies the state, and only the VALUE is chosen. */
  const [ownQuery, setOwnQuery] = useState<string | null>(null);
  const q = query === undefined ? ownQuery : query;
  const setQ = setQuery === undefined ? setOwnQuery : setQuery;

  /* 134 §1(e) — the completed text is STATE, decided in onChange where the verb
     is in hand; a keystroke completes, a backspace must not. */
  const [completed, setCompleted] = useState<string | null>(null);
  /* 186 A.3a — the document the menu portals into: the input's OWN, captured on
     focus (a user act precedes every open, C18) rather than read off the ref in
     render. A host portaled into a pop-out window — the sidebar chats, the
     compact column — gets its menu in THAT window, not in the opener's
     (MentionInput's rule, ChatView). */
  const [menuDoc, setMenuDoc] = useState<Document | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  /* 204 §1 — inside a ModalLayout LIFTED over an open pane the menu and its
     backdrop take one over the lifted shell; otherwise the two module
     constants as they are (same objects, so nothing re-applies). */
  const menuZ = useMenuLevel(Z.popover);
  const menuStyle: CSSProperties = menuZ === Z.popover ? MENU_BOX : { ...MENU_BOX, zIndex: menuZ };
  const backdropStyle: CSSProperties = menuZ === Z.popover ? BACKDROP : { ...BACKDROP, zIndex: menuZ - 1 };

  /* (c) — typing searches label AND mono (a matter's title and its ref; a
     lawyer types `2026/001` as readily as a name). */
  const matchesFor = (text: string) => {
    const sq = text.toLowerCase();
    return options.filter((o) => (o.label + (o.mono || "")).toLowerCase().includes(sq));
  };

  /* 133 §1 / 134 §1(b) — what an open menu shows: the recents where they
     exist, else the first five of the same filtered population; never empty,
     never a wall. `list.length === 0` still means the population itself is
     empty, and the menu stays absent (unless a clear row gives it something
     to show). */
  const model = useMemo(() => {
    if (q == null) return null;
    const sq = (q || "").toLowerCase();
    if (!sq) {
      const all = getRecents ? getRecents() : [];
      /* 151C §1 — recents LEAD, they do not REPLACE: the complete population
         follows them, so nothing is ever one typed letter away from
         invisible. The legacy arm stays the either/or the matter pickers
         ruled. */
      if (fullListOnOpen) {
        return {
          list: options,
          recents: all.slice(0, OPEN_RECENTS_CAP),
          isRecents: false,
          hiddenCount: 0,
          sole: null as T | null,
        };
      }
      const recents = all.slice(0, CLICK_CAP);
      const list = recents.length ? recents : options.slice(0, CLICK_CAP);
      return {
        list,
        recents: [] as T[],
        isRecents: recents.length > 0,
        hiddenCount: options.length - list.length,
        sole: null as T | null,
      };
    }
    const hits = matchesFor(sq);
    return {
      list: hits.slice(0, SEARCH_CAP),
      recents: [] as T[],
      isRecents: false,
      hiddenCount: hits.length - Math.min(hits.length, SEARCH_CAP),
      sole: hits.length === 1 ? hits[0] : null,
    };
  }, [q, options, getRecents, fullListOnOpen]);

  const menuOpen = !!model && (!!clearLabel || model.list.length > 0);

  /* 151D §1(b)+(e) — the menu wears the house scroll affordance: the
     modals' own hook/arrows, attached on open (the portal exists only
     then — attachKey). The DOWN arrow is what makes "the list continues"
     legible before any scroll happens. */
  const menuHint = useScrollHint(menuOpen);

  useAnchoredMenu({
    open: menuOpen,
    anchorRef: inputRef,
    menuRef,
    onDismiss: () => {
      setQ(null);
      setCompleted(null);
    },
    deps: [model ? model.list.length : 0, !!clearLabel, model ? model.hiddenCount > 0 : false],
    minWidth: MENU_MIN_W,
  });

  /* A completion that no longer fits its query cannot render — the query is
     lifted for some hosts and written from outside the picker. */
  const shownCompletion =
    completed &&
    q != null &&
    completed.length > q.length &&
    completed.toLowerCase().startsWith(q.toLowerCase())
      ? completed
      : null;

  /* (e) — with the completed part selected, so the next keystroke replaces it. */
  useLayoutEffect(() => {
    if (!shownCompletion) return;
    const el = inputRef.current;
    if (!el || document.activeElement !== el) return;
    el.setSelectionRange((q || "").length, shownCompletion.length);
  }, [shownCompletion]);

  const commit = (o: T) => {
    setQ(null);
    setCompleted(null);
    onPick(o);
  };

  /* One row, one renderer — the full-list arm shows an option under Recent AND
     in the complete list below, and siblings of one parent need distinct keys,
     hence the prefix. Lowercase helper invoked as a call (Bug-A). */
  const optionRow = (o: T, keyPrefix = "") => (
    <div
      key={keyPrefix + o.key}
      style={{
        padding: "6px 10px",
        cursor: "pointer",
        fontSize: FS.s10_5,
        display: "flex",
        gap: 6,
        alignItems: "center",
      }}
      onMouseEnter={(e2) => (e2.currentTarget.style.background = "var(--bh)")}
      onMouseLeave={(e2) => (e2.currentTarget.style.background = "none")}
      onClick={() => commit(o)}
    >
      <span
        style={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {o.label}
      </span>
      {o.mono != null && (
        <span
          style={{
            fontSize: FS.s9,
            color: "var(--tm)",
            flexShrink: 0,
            fontFamily: "'JetBrains Mono',monospace",
          }}
        >
          {o.mono}
        </span>
      )}
    </div>
  );

  /* 210 §1 — after every hook, so the picker's own state lives on through a
     retry: a population with a list missing is not offered. */
  if (unloaded.length)
    return (
      <div style={{ position: "relative", ...(wrapperStyle ?? {}) }}>
        <ListNotLoaded lists={unloaded} variant="inline" />
      </div>
    );

  return (
    <div style={{ position: "relative", ...(wrapperStyle || {}) }}>
      <input
        ref={inputRef}
        /* 213B §8 — not a sign-in field: no AutoFill of sign-ins or contacts
           (src/lib/noAutofill.ts says what Safari honours, and what it reads) */
        {...NO_AUTOFILL}
        aria-autocomplete="list"
        className={noAutofillClass(inputClassName)}
        style={inputStyle === undefined ? { padding: "5px 8px", fontSize: FS.s11 } : inputStyle}
        placeholder={placeholder}
        value={shownCompletion ?? (q || selectedLabel || "")}
        onChange={(e) => {
          const v = e.target.value;

          /* 134 §1(e) — deleting never completes, and the signal is
             `InputEvent.inputType`, not a length comparison: Backspace over the
             selection and typing a letter into it both SHORTEN the value, and
             only one may re-complete. */
          const verb = (e.nativeEvent as InputEvent).inputType || "";
          const deleting = verb
            ? verb.startsWith("delete")
            : v.length < (shownCompletion ?? q ?? "").length;

          setQ(v);
          if (!v) {
            setCompleted(null);
            onClear();
            return;
          }

          /* It completes the TEXT; it does not commit the CHOICE (Enter or a
             click commits). Prefix only: a mid-label or mono match keeps what
             was typed and the one row waits in the menu. */
          const hits = matchesFor(v);
          const sole = hits.length === 1 ? hits[0] : null;
          const label = sole ? sole.label || "" : "";
          setCompleted(
            !deleting && sole && label.toLowerCase().startsWith(v.toLowerCase()) && label.length > v.length
              ? v + label.slice(v.length)
              : null,
          );
        }}
        onFocus={(e) => {
          setMenuDoc(e.currentTarget.ownerDocument);
          /* C18 — opening the dropdown is a user act (focus), never automatic. */
          if (q == null) setQ("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setQ(null);
            setCompleted(null);
            (e.target as HTMLInputElement).blur();
            return;
          }
          /* (e) — Enter commits the SINGLE remaining match; with more than one
             it does nothing and the typing continues. */
          if (e.key === "Enter" && q) {
            const hits = matchesFor(q);
            if (hits.length === 1) {
              e.preventDefault();
              commit(hits[0]);
            }
          }
        }}
      />
      {menuOpen &&
        model &&
        createPortal(
          <>
            <div
              style={backdropStyle}
              onMouseDown={() => {
                setQ(null);
                setCompleted(null);
              }}
            />
            <div
              ref={(el) => {
                menuRef.current = el;
                menuHint.ref.current = el;
              }}
              style={menuStyle}
            >
              {menuHint.above && <ScrollHint dir="up" />}
              {clearLabel && (
                <div
                  style={{
                    padding: "6px 10px",
                    cursor: "pointer",
                    fontSize: FS.s10_5,
                    color: "var(--tm)",
                  }}
                  onMouseEnter={(e2) => (e2.currentTarget.style.background = "var(--bh)")}
                  onMouseLeave={(e2) => (e2.currentTarget.style.background = "none")}
                  onClick={() => {
                    onClear();
                    setQ(null);
                    setCompleted(null);
                  }}
                >
                  {clearLabel}
                </div>
              )}
              {(model.isRecents || model.recents.length > 0) && (
                <div style={HEAD_ROW}>{t("common.recent", { fallback: "Recent" })}</div>
              )}
              {model.recents.map((o) => optionRow(o, "r:"))}
              {/* 151D §1(c–e) — the recents/full-list boundary is a SUBTLE
                  LINE and nothing else: the 2px margins that read as a blank
                  gap between two rows are gone; the row rhythm is unchanged
                  across the line. */}
              {model.recents.length > 0 && (
                <div aria-hidden style={{ borderTop: "1px solid var(--br)" }} />
              )}
              {model.list.map((o) => optionRow(o))}
              {model.hiddenCount > 0 && <div style={MORE_ROW}>{moreLabel(model.hiddenCount)}</div>}
              {menuHint.below && <ScrollHint dir="down" />}
            </div>
          </>,
          menuDoc?.body ?? document.body,
        )}
    </div>
  );
}
